import { ReactNode } from 'react';

// Renders the inline markdown that shows up in prose paragraphs — bold, italic,
// bold-italic, and inline code — into React nodes. It also returns the plain
// text (markers removed), which is what the reader's word-offset math and
// selection rely on: the rendered DOM's textContent must equal the block's
// `text`, or scroll-restore and phrase selection drift by the marker count.
export interface InlineRendered {
  nodes: ReactNode[];
  plain: string;
}

interface Rule {
  re: RegExp;
  wrap: (children: ReactNode[], key: number) => ReactNode;
}

// Order matters: longer markers are tried first so `***x***` beats `**x**`
// beats `*x*`. Ties on the same start index resolve to the earlier rule.
const RULES: Rule[] = [
  {
    re: /\*\*\*([\s\S]+?)\*\*\*/,
    wrap: (c, k) => (
      <strong key={k}>
        <em>{c}</em>
      </strong>
    ),
  },
  {
    re: /___([\s\S]+?)___/,
    wrap: (c, k) => (
      <strong key={k}>
        <em>{c}</em>
      </strong>
    ),
  },
  { re: /\*\*([\s\S]+?)\*\*/, wrap: (c, k) => <strong key={k}>{c}</strong> },
  { re: /__([\s\S]+?)__/, wrap: (c, k) => <strong key={k}>{c}</strong> },
  { re: /\*([\s\S]+?)\*/, wrap: (c, k) => <em key={k}>{c}</em> },
  { re: /_([\s\S]+?)_/, wrap: (c, k) => <em key={k}>{c}</em> },
  {
    re: /`([^`]+?)`/,
    wrap: (c, k) => (
      <code
        key={k}
        className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[0.9em] dark:bg-slate-800"
      >
        {c}
      </code>
    ),
  },
];

function walk(text: string, counter: { n: number }): InlineRendered {
  // Find the earliest complete marker across all rules; everything before it is
  // literal (it holds no complete pair, or that pair would have matched sooner).
  let best: { rule: Rule; index: number; match: RegExpExecArray } | null = null;
  for (const rule of RULES) {
    const m = rule.re.exec(text);
    if (m && (best === null || m.index < best.index)) {
      best = { rule, index: m.index, match: m };
    }
  }
  if (!best) return { nodes: text ? [text] : [], plain: text };

  const { rule, index, match } = best;
  const before = text.slice(0, index);
  const inner = walk(match[1], counter); // allow nesting (e.g. **a _b_**)
  const rest = walk(text.slice(index + match[0].length), counter);
  const key = counter.n;
  counter.n += 1;

  const nodes: ReactNode[] = [];
  if (before) nodes.push(before);
  nodes.push(rule.wrap(inner.nodes, key));
  nodes.push(...rest.nodes);
  return { nodes, plain: before + inner.plain + rest.plain };
}

export function renderInlineMarkdown(text: string): InlineRendered {
  return walk(text, { n: 0 });
}
